import {
  CircularProgress,
  Grid,
  Typography,
  Box,
  TextField,
  Autocomplete,
  AutocompleteProps,
  InputAdornment,
  Avatar,
  ListSubheader,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import { FormikProps, getIn } from "formik";
import { FieldOption } from "../FieldOption";
import { FormField } from "../FormField";
import { FixedLabel, maybePadRight } from "../Functions";
import React, { useEffect, useMemo, useState, useRef } from "react";
import debounce from "lodash/debounce";
import useApi from "../../../hooks/useApi";

const PAGE_SIZE = 25;

function PaginatedMultipleAutocompleteField(props: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
  classes: Record<string, string>;
  variant: "filled" | "outlined" | "standard";
}) {
  const { formik, field, classes, variant } = props;

  const [options, setOptions] = useState<FieldOption[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const api = useApi();

  const listboxRef = useRef<HTMLUListElement | null>(null);

  // Get current Formik-selected values
  const selectedValues = getIn(formik.values, field.id) || [];

  // Debounced fetch function for API and static choices
  const fetchOptions = useMemo(
    () =>
      debounce(async (search: string, pageNumber: number) => {
        setLoading(true);
        try {
          let data: FieldOption[] = [];

          // If search is empty and choices are predefined, paginate them
          if (search.trim() === "" && field.choices) {
            data = field.choices.slice(
              pageNumber * PAGE_SIZE,
              (pageNumber + 1) * PAGE_SIZE,
            );
          } else if (search.trim() !== "") {
            // Real API call for dynamic search.
            //
            // NEW-21: with `searchMethod: "post"` the term goes in a BODY, not a URL. The one
            // field using this component searches the message-recipient roster, which spans
            // staff and patient users, and it fires on a 100 ms debounce as the user types — so
            // on a GET each keystroke-prefix of a patient's name was written to the ALB access
            // log, outside the audited PHI perimeter. Same device as MtcList's `searchMethod`.
            const response =
              field.searchMethod === "post"
                ? await api.post<{ data: FieldOption[] }>(
                    `${field.searchApi}/search`,
                    { q: search, pageNumber, pageSize: PAGE_SIZE },
                  )
                : await api.get<{ data: FieldOption[] }>(
                    `${field.searchApi}?q=${encodeURIComponent(search)}&page=${pageNumber}&size=${PAGE_SIZE}`,
                  );
            data = response.data.data;
          }

          // Append or replace options based on page
          setOptions((prev) => (pageNumber === 0 ? data : [...prev, ...data]));
          setHasMore(data.length === PAGE_SIZE); // Check if more pages exist
        } catch (error) {
          console.error("Failed to fetch options", error);
          if (pageNumber === 0) setOptions([]);
          setHasMore(false);
        } finally {
          setLoading(false);
        }
      }, 100),
    [field.choices, field.searchApi, api],
  );

  // Cleanup debounce on unmount
  useEffect(() => fetchOptions.cancel(), [fetchOptions]);

  // Fetch data when inputValue changes
  useEffect(() => {
    setPage(0);
    fetchOptions(inputValue, 0);
  }, [inputValue, fetchOptions]);

  // Optional initial fetch for first page (empty search)
  useEffect(() => {
    fetchOptions("", 0);
  }, [fetchOptions]);

  // Scroll handler for infinite scrolling
  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 5 &&
      !loading &&
      hasMore
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchOptions(inputValue, nextPage);
    }
  };

  // Handle value change
  const onChangeHandler = (
    _e: React.SyntheticEvent,
    newValue: FieldOption[],
  ) => {
    formik.setFieldValue(field.id, newValue || []);
  };

  // Option label rendering
  const getOptionLabel = (option: FieldOption | string) => {
    if (typeof option === "string") {
      return field.choices?.find((choice) => choice.id === option)?.text || "";
    } else {
      return option.text || option.name;
    }
  };

  // Equality check for options and values
  const isEqualChecker = (option: FieldOption, value: FieldOption | string) => {
    if (typeof value === "string") {
      return option.id === value;
    }
    return option.id === value.id;
  };

  // Merge current options with selected values to ensure visibility
  const mergedOptions = Array.from(
    new Map(
      [...options, ...selectedValues].map((item) => [item.id, item]),
    ).values(),
  );

  const sortedOptions = field.groupChoices
    ? mergedOptions.sort((a, b) =>
        a.group < b.group ? -1 : a.group > b.group ? 1 : 0,
      )
    : mergedOptions;

  // For grouped pickers (e.g. message recipients: "People" vs "Groups") we render Slack/Teams-style
  // rows: an avatar with initials for individual people, a group icon for role groups.
  const initialsOf = (label: string) =>
    label
      .split(" ")
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const isPeopleGroup = (option: FieldOption) =>
    (option.group ?? "").toLowerCase().includes("people");

  const optionAvatar = (option: FieldOption, label: string) =>
    isPeopleGroup(option) ? (
      <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
        {initialsOf(label) || "?"}
      </Avatar>
    ) : (
      <Avatar sx={{ width: 28, height: 28, bgcolor: "secondary.main" }}>
        <GroupIcon sx={{ fontSize: 16 }} />
      </Avatar>
    );

  type ACProps = AutocompleteProps<FieldOption, true, false, false>;

  const renderGroupedOption: ACProps["renderOption"] = (
    optionProps,
    option,
  ) => {
    const label = getOptionLabel(option);
    const { key, ...rest } = optionProps;
    return (
      <Box
        component="li"
        key={key}
        {...rest}
        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
      >
        {optionAvatar(option, label)}
        <Typography variant="body2">{label}</Typography>
      </Box>
    );
  };

  const renderGroupedGroup: ACProps["renderGroup"] = (params) => (
    <li key={params.key}>
      <ListSubheader
        disableSticky
        sx={{
          bgcolor: "transparent",
          fontWeight: 700,
          fontSize: "0.7rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          lineHeight: "2rem",
        }}
      >
        {params.group}
      </ListSubheader>
      <Box component="ul" sx={{ p: 0 }}>
        {params.children}
      </Box>
    </li>
  );

  // === Final Render ===

  return (
    <React.Fragment key={field.id}>
      <Grid size={{ xs: 12, sm: field.columns || 6 }}>
        {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
        <Box sx={{ display: "flex" }}>
          <Box sx={{ flexGrow: 1 }}>
            <Autocomplete<FieldOption, true, false, false>
              multiple
              id={field.id}
              groupBy={
                field.groupChoices
                  ? (option: FieldOption) => option.group
                  : undefined
              }
              value={selectedValues}
              onChange={onChangeHandler}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isEqualChecker}
              filterOptions={(x) => x} // Disable built-in filtering
              options={sortedOptions}
              onInputChange={(_event, newInputValue, reason) => {
                if (reason === "input") setInputValue(newInputValue);
              }}
              renderOption={
                field.groupChoices ? renderGroupedOption : undefined
              }
              renderGroup={field.groupChoices ? renderGroupedGroup : undefined}
              slotProps={{
                listbox: {
                  onScroll: handleScroll,
                  ref: listboxRef,
                  style: { maxHeight: 300, overflow: "auto" },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={!field.fixedLabel && field.text}
                  variant={variant}
                  className={classes.formControl}
                  fullWidth
                  onBlur={formik.handleBlur}
                  helperText={
                    getIn(formik.touched, field.id) ? (
                      <>{getIn(formik.errors, field.id) as string}</>
                    ) : (
                      ""
                    )
                  }
                  error={
                    getIn(formik.touched, field.id) &&
                    Boolean(getIn(formik.errors, field.id))
                  }
                  slotProps={{
                    ...params.slotProps,

                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <InputAdornment position="end">
                          {loading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Grid>
      {maybePadRight(field)}
    </React.Fragment>
  );
}

export default PaginatedMultipleAutocompleteField;
